import { CACHE_TAGS_HEADER as TAGS_HEADER } from '~lib';

/**
 * @desc The zod-free half of the insights fragment contract (round 3 lane 8, H2-312): the route
 * paths, the colour-scheme parameter and the frame-summary attributes — everything a client needs
 * to address and read a fragment, without its JSON schemas. The portal's entry chunk imports this
 * module and never `fragments.model.ts`, whose schemas would put Zod (and the content and markets
 * models) on every first load; the decoders that need them load lazily. `Fragments` re-exports
 * every member, so producers and the schemas keep one name.
 */
export namespace FragmentRoutes {
  export const PREFIX = '/fragments';

  /** @desc The portal's selectable schemes (`apps/portal/src/lib/charlie-scheme.ts`). */
  export const colourSchemes = ['light', 'dark', 'blue'] as const;
  export type ColourScheme = (typeof colourSchemes)[number];
  /** @desc What a component route renders for a missing or unknown scheme — insights' own default. */
  export const DEFAULT_COLOUR_SCHEME: ColourScheme = 'dark';
  /** @desc The search parameter the loader sets on every component route. */
  export const SCHEME_PARAM = 'scheme';

  /**
   * @desc What a component fragment rendered, stated on its root element so the portal can give the
   * section the page's own treatment (hide it when empty, its own error state when unavailable,
   * the page notice and "Last updated") without a second read of the same content. `ready` drew
   * `items` entries, `illustrative` of them seeded sample content, the newest dated `latest`;
   * `empty` had nothing to draw; `unavailable` means the upstream could not answer (logged on
   * insights). A fragment without these attributes (the operator pages, or an insights deploy that
   * predates them) states nothing, and the portal renders it as is.
   */
  export const frameStates = ['ready', 'empty', 'unavailable'] as const;
  export type FrameState = (typeof frameStates)[number];
  export type FrameSummary = {
    state: FrameState;
    items: number;
    illustrative: number;
    latest: Date | null;
  };

  /** @desc The root-element attributes that carry a {@link FrameSummary}. */
  export const FRAME_ATTRIBUTES = {
    state: 'data-fragment-state',
    items: 'data-fragment-items',
    illustrative: 'data-fragment-illustrative',
    latest: 'data-fragment-latest',
  } as const;

  /** @desc A summary as the attributes the fragment root carries (producer side). */
  export function frameAttributes(summary: FrameSummary): Record<string, string> {
    return {
      [FRAME_ATTRIBUTES.state]: summary.state,
      [FRAME_ATTRIBUTES.items]: String(summary.items),
      [FRAME_ATTRIBUTES.illustrative]: String(summary.illustrative),
      ...(summary.latest ? { [FRAME_ATTRIBUTES.latest]: summary.latest.toISOString() } : {}),
    };
  }

  const count = (value: string | null) => {
    const n = Number(value);
    return value !== null && Number.isInteger(n) && n >= 0 ? n : 0;
  };

  /**
   * @desc Reads a {@link FrameSummary} back from an element's attributes (consumer side), or
   * `undefined` when the element states no known state. Plain code rather than a Zod schema: it
   * runs in the portal's fragment loader, which stays light.
   */
  export function readFrameSummary(
    getAttribute: (name: string) => string | null,
  ): FrameSummary | undefined {
    const state = getAttribute(FRAME_ATTRIBUTES.state);
    if (!frameStates.some((known) => known === state)) return undefined;
    const latestValue = getAttribute(FRAME_ATTRIBUTES.latest);
    const latest = latestValue ? new Date(latestValue) : null;
    return {
      state: state as FrameState,
      items: count(getAttribute(FRAME_ATTRIBUTES.items)),
      illustrative: count(getAttribute(FRAME_ATTRIBUTES.illustrative)),
      latest: latest && !Number.isNaN(latest.getTime()) ? latest : null,
    };
  }

  /** @desc Response header listing the cache tags a fragment was rendered from (comma-separated). */
  export const CACHE_TAGS_HEADER = TAGS_HEADER;

  const encode = (slug: string) => encodeURIComponent(slug);

  /** @desc Route paths, relative to the insights origin (or the portal's `/fragments` rewrite). */
  export const paths = {
    component: {
      analystNoteLatest: '/fragments/c/content/analyst-note/latest',
      quarterUpdates: (group: string) => `/fragments/c/content/quarter-updates/${encode(group)}`,
      callSummaries: (group: string) => `/fragments/c/content/call-summaries/${encode(group)}`,
      /** @desc Filtered by the optional `group` and `market` search parameters. */
      marketUpdates: '/fragments/c/content/market-updates',
    },
    data: {
      analystNoteLatest: '/fragments/d/content/analyst-note/latest',
      quarterUpdates: (group: string) => `/fragments/d/content/quarter-updates/${encode(group)}`,
      callSummaries: (group: string) => `/fragments/d/content/call-summaries/${encode(group)}`,
      /** @desc Filtered by the optional `group` and `market` search parameters. */
      marketUpdates: '/fragments/d/content/market-updates',
      annualGgr: (market: string) => `/fragments/d/markets/${encode(market)}/annual-ggr`,
    },
  } as const;
}
